import React, { useState } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { signIn } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { BRAND, PRODUCT } from '../config';

interface LoginPageProps {
  onLogin: () => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');
    try {
      await signIn(email.trim(), password);
      onLogin();
    } catch {
      setError('E-Mail oder Passwort falsch');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError('Bitte gib zuerst deine E-Mail-Adresse ein.');
      return;
    }
    setResetting(true);
    try {
      await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      // Generic message — never reveal whether an account exists.
      setInfo('Falls ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet.');
    } catch {
      setInfo('Falls ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: 'primary.main',
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {PRODUCT === 'skonto' ? (
              <ReceiptLongIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
            ) : (
              <RestaurantIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
            )}
            <Typography variant="h5" fontWeight={700}>{BRAND.name}</Typography>
            <Typography variant="body2" color="text.secondary">{BRAND.subtitle}</Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              label="E-Mail"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              label="Passwort"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Anmelden'}
            </Button>
            <Button
              type="button"
              variant="text"
              fullWidth
              size="small"
              onClick={handleForgot}
              disabled={resetting}
              sx={{ mt: 1.5, color: 'text.secondary' }}
            >
              {resetting ? 'Wird gesendet…' : 'Passwort vergessen?'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
